import { TaskContext } from "contexts/TaskDetailContext";
import { useCallback, useContext, useEffect, useState } from "react";
import { getQuestions } from "services/models/modelDetails/modelTasks/questions";
import { useLocation, useParams } from "react-router-dom";
import ColourConstants from "helpers/colourConstants";
import HtmlTooltip from "components/Elements/HtmlTooltip";
import ErrorMessageWithErrorIcon from "components/Elements/ErrorMessageWithErrorIcon";
import { ModelContext } from "contexts/ModelDetailContext";
import i18n from "i18n";

const questionTypeOptions = () => [
	{ label: i18n.t("option.checkbox"), value: "B" },
	{ label: i18n.t("option.checkboxList"), value: "C" },
	{ label: i18n.t("label.date"), value: "D" },
	{ label: i18n.t("option.dropdown"), value: "O" },
	{ label: i18n.t("option.longText"), value: "L" },
	{ label: i18n.t("option.number"), value: "N" },
	{ label: i18n.t("option.shortText"), value: "S" },
	{ label: i18n.t("option.time"), value: "T" },
];

export default function useModelTaskQuestions({
	taskInfo,
	getError,
	isMounted,
}) {
	const [state, MtxDispatch] = useContext(ModelContext);
	const [, CtxDispatch] = useContext(TaskContext);
	const location = useLocation();
	const { id } = useParams();

	// Initiate States
	const [data, setData] = useState([]);
	const [originalList, setOriginalList] = useState([]);
	const [loading, setLoading] = useState({
		fetch: true,
		loader: false,
	});
	// ARRANGING DATA FROM THE API RESPONSE

	// HANDLE ERROR FROM API RESPONSE
	const errorResponse = (result) => {
		if (result.data.detail) getError(result.data.detail);
		else getError(i18n.t("message.somethingWentWrongExclaim"));
	};

	// memoized fxn
	const apiResponse = useCallback((d) => {
		const question = questionTypeOptions().find((a) => a.value === d.type);
		const options = d?.options?.map((a, index) => (
			<span
				key={a?.id}
				style={{
					color: a?.raiseDefect ? ColourConstants.red : "",
				}}
			>
				{d?.options?.length - 1 === index ? a?.name + "" : a?.name + ", "}
			</span>
		));
		const res = {
			...d,
			questionType: question.label,
			compulsory: d.isCompulsory ? i18n.t("label.yes") : i18n.t("label.no"),
			captureImageDisplay: d.captureImage ? i18n.t("label.yes") : i18n.t("label.no"),
			alertDisplay: d.enableConditionMonitoringAlert
				? i18n.t("label.yes")
				: i18n.t("label.no"),
			additional:
				d.type === "B" ? (
					<span>
						<strong>{i18n.t("label.checkboxCaption")} : </strong>&nbsp;
						{d.checkboxCaption}
					</span>
				) : d.type === "N" ? (
					<span style={{ display: "flex", gap: 10 }}>
						<span>
							<strong>{i18n.t("label.decimalPlaces")} : </strong>
							{d.decimalPlaces}
						</span>
						{d.minValue !== null ? (
							<span>
								<strong>{i18n.t("label.minimumValue")} : </strong>
								{d.minValue}
							</span>
						) : (
							""
						)}
						{d.maxValue !== null ? (
							<span>
								<strong>{i18n.t("label.maximumValue")} : </strong>
								{d.maxValue}
							</span>
						) : (
							""
						)}
					</span>
				) : d.type === "C" || d.type === "O" ? (
					<>
						<HtmlTooltip title={options}>
							<p className="max-two-line">
								<span>
									{d?.options === null || d?.options?.length === 0 ? (
										<ErrorMessageWithErrorIcon
											message={i18n.t("message.noOptionsAssigned")}
										/>
									) : (
										<>
											<strong>{i18n.t("label.options")} : </strong>&nbsp;
											{options}
										</>
									)}
								</span>
							</p>
						</HtmlTooltip>
					</>
				) : (
					""
				),
		};
		return res;
	}, []);

	const fetchTaskQuestion = async () => {
		try {
			let result = await getQuestions(taskInfo.id);
			if (result.status) {
				if (!isMounted.aborted) {
					result = result.data.map((x) => apiResponse(x));
					setData(result);
					setOriginalList(result);
				}
				CtxDispatch({
					type: "TAB_COUNT",
					payload: {
						countTab: "questionCount",
						data: result?.length,
					},
				});
				if (result.length > 0) {
					CtxDispatch({
						type: "SET_QUESTIONS",
						payload: true,
					});
				} else {
					CtxDispatch({
						type: "SET_QUESTIONS",
						payload: false,
					});
				}
			} else {
				errorResponse(result);
			}
		} catch (e) {
			return;
		}
	};

	useEffect(() => {
		const selectedQuestion = document.getElementById(
			`row${location.state?.modelVersionQuestionID}`
		);
		if (
			!loading.fetch &&
			location.state?.modelVersionQuestionID &&
			selectedQuestion
		) {
			selectedQuestion.style.background = "#ffeb3b";
			selectedQuestion.scrollIntoView({ block: "center", behavior: "smooth" });
		}
	}, [loading, location.state]);

	useEffect(() => {
		const initialFetch = async () => {
			setLoading((th) => ({ ...th, fetch: true }));
			await fetchTaskQuestion();
			setLoading((th) => ({ ...th, fetch: false }));
		};
		initialFetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		CtxDispatch,
		MtxDispatch,
		data,
		errorResponse,
		fetchTaskQuestion,
		id,
		loading,
		originalList,
		setData,
		setLoading,
		setOriginalList,
		state,
	};
}
